import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTheme } from '../context/ThemeContext';
import { authFetch, authJsonFetch, getAuthHeaders } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';
import { Slider } from './ui/slider';
import { 
  Send, 
  X, 
  Minimize2, 
  Download, 
  Loader2,
  Sparkles,
  Trash2,
  MessageCircle,
  Settings,
  GripVertical
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

const API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '')}/api`;

// Theme-aware assistant avatar SVG component
const AssistantAvatar = ({ isAnimated = false, size = 'md', isBarbiePink = false }) => {
  const sizes = {
    sm: 'w-10 h-10',
    md: 'w-16 h-16',
    lg: 'w-20 h-20'
  };
  
  // Theme colors
  const theme = isBarbiePink ? {
    bgFrom: 'from-pink-100',
    bgTo: 'to-pink-200',
    hair1: '#FFB6C1', // Light pink hair
    hair2: '#FF69B4', // Hot pink
    bgGrad1: '#FFE4EC',
    bgGrad2: '#FFB6D9',
    cheeks: '#FF91A4',
    lipColor: '#E75480'
  } : {
    bgFrom: 'from-amber-100',
    bgTo: 'to-amber-200',
    hair1: '#F5D89A', // Blonde
    hair2: '#E8C86D', // Golden blonde
    bgGrad1: '#E8F4F8',
    bgGrad2: '#C5E1E9',
    cheeks: '#FFCCC8',
    lipColor: '#CC8080'
  };
  
  return (
    <div className={cn(
      sizes[size],
      `relative rounded-full overflow-hidden bg-gradient-to-br ${theme.bgFrom} ${theme.bgTo} shadow-lg`,
      isAnimated && "animate-pulse"
    )}>
      <svg viewBox="0 0 100 100" className="w-full h-full">
        {/* Background circle */}
        <circle cx="50" cy="50" r="50" fill={`url(#avatarGradient-${isBarbiePink ? 'pink' : 'default'})`} />
        
        {/* Hair - flowing */}
        <ellipse cx="50" cy="35" rx="35" ry="28" fill={theme.hair1} />
        <ellipse cx="50" cy="45" rx="38" ry="30" fill={theme.hair2} />
        <path d="M15 50 Q20 70 30 85 L25 90 Q15 70 12 50Z" fill={theme.hair2} />
        <path d="M85 50 Q80 70 70 85 L75 90 Q85 70 88 50Z" fill={theme.hair2} />
        
        {/* Face */}
        <ellipse cx="50" cy="55" rx="25" ry="28" fill="#FDEBD8" />
        
        {/* Rosy cheeks */}
        <circle cx="32" cy="60" r="5" fill={theme.cheeks} opacity="0.6" />
        <circle cx="68" cy="60" r="5" fill={theme.cheeks} opacity="0.6" />
        
        {/* Eyes */}
        <ellipse cx="38" cy="52" rx="5" ry="4" fill={isBarbiePink ? "#5D3A6E" : "#4A3728"} />
        <ellipse cx="62" cy="52" rx="5" ry="4" fill={isBarbiePink ? "#5D3A6E" : "#4A3728"} />
        {/* Eye sparkle */}
        <circle cx="36" cy="51" r="1.5" fill="white" />
        <circle cx="60" cy="51" r="1.5" fill="white" />
        
        {/* Eyebrows */}
        <path d="M30 46 Q38 43 44 46" stroke={isBarbiePink ? "#9B6B8E" : "#B8956C"} strokeWidth="1.5" fill="none" />
        <path d="M56 46 Q62 43 70 46" stroke={isBarbiePink ? "#9B6B8E" : "#B8956C"} strokeWidth="1.5" fill="none" />
        
        {/* Nose */}
        <path d="M50 55 Q52 60 50 63" stroke="#E0C4A8" strokeWidth="1.5" fill="none" />
        
        {/* Smile */}
        <path d="M40 68 Q50 76 60 68" stroke={theme.lipColor} strokeWidth="2" fill="none" strokeLinecap="round" />
        
        {/* Hair bangs */}
        <path d="M25 38 Q35 25 50 28 Q65 25 75 38 L72 42 Q60 32 50 35 Q40 32 28 42Z" fill={theme.hair1} />
        
        {/* Accessories for pink theme */}
        {isBarbiePink && (
          <>
            <circle cx="25" cy="35" r="3" fill="#FF1493" opacity="0.8" />
            <circle cx="75" cy="35" r="3" fill="#FF1493" opacity="0.8" />
          </>
        )}
        
        <defs>
          <linearGradient id="avatarGradient-default" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#E8F4F8" />
            <stop offset="100%" stopColor="#C5E1E9" />
          </linearGradient>
          <linearGradient id="avatarGradient-pink" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#FFE4EC" />
            <stop offset="100%" stopColor="#FFB6D9" />
          </linearGradient>
        </defs>
      </svg>
      
      {/* Status indicator */}
      {isAnimated && (
        <span className={cn(
          "absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-white animate-pulse",
          isBarbiePink ? "bg-pink-400" : "bg-green-400"
        )} />
      )}
    </div>
  );
};

// Mini avatar for minimized state - now draggable with breathing glow
const MiniAssistant = ({ onClick, hasNewMessage, position, onDragStart, isBarbiePink, isConnected }) => {
  return (
    <div
      style={{ left: position.x, bottom: position.y }}
      className="fixed z-50"
    >
      <button
        onClick={onClick}
        onMouseDown={onDragStart}
        onTouchStart={onDragStart}
        className={cn(
          "group transition-all duration-300 ease-out cursor-grab active:cursor-grabbing",
          "hover:scale-110 active:scale-95"
        )}
        data-testid="ai-assistant-mini-btn"
      >
        <div className="relative">
          {/* Breathing glow effect when connected */}
          {isConnected && (
            <div className={cn(
              "absolute inset-0 rounded-full",
              isBarbiePink 
                ? "bg-pink-400/50 animate-[breathing-glow_3s_ease-in-out_infinite]" 
                : "bg-green-400/50 animate-[breathing-glow_3s_ease-in-out_infinite]"
            )} 
            style={{
              animation: 'breathing-glow 3s ease-in-out infinite',
              transform: 'scale(1.3)',
              filter: 'blur(8px)'
            }}
            />
          )}
          
          <AssistantAvatar size="md" isBarbiePink={isBarbiePink} />
          
          {/* Connection status dot */}
          <div className={cn(
            "absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-white flex items-center justify-center",
            isConnected ? "bg-green-500" : "bg-orange-500"
          )}>
            {isConnected ? (
              <span className="w-2 h-2 bg-white rounded-full" />
            ) : (
              <span className="text-white text-[8px] font-bold">!</span>
            )}
          </div>
          
          {/* Speech bubble indicator */}
          <div className={cn(
            "absolute -top-2 -right-2 rounded-full p-1.5",
            "transform transition-transform",
            isBarbiePink ? "bg-pink-500 text-white" : "bg-primary text-primary-foreground",
            hasNewMessage ? "scale-100 animate-bounce" : "scale-0"
          )}>
            <MessageCircle className="w-3 h-3" />
          </div>
          
          {/* Tooltip */}
          <div className={cn(
            "absolute left-full ml-3 top-1/2 -translate-y-1/2 px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none shadow-lg",
            isBarbiePink ? "bg-pink-500 text-white" : "bg-foreground text-background"
          )}>
            {isConnected ? 'Chat with Sophie (Connected)' : 'Chat with Sophie (Limited)'}
            <div className={cn(
              "absolute right-full top-1/2 -translate-y-1/2 border-8 border-transparent",
              isBarbiePink ? "border-r-pink-500" : "border-r-foreground"
            )} />
          </div>
        </div>
      </button>
    </div>
  );
};

export const AIAssistant = () => {
  const { isBarbiePink } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [hasNewMessage, setHasNewMessage] = useState(false);
  const [temperature, setTemperature] = useState(0.1);
  const [savingSettings, setSavingSettings] = useState(false);
  
  // AI Model state
  const [aiModel, setAiModel] = useState({
    provider: 'gemini',
    model: 'gemini-3-flash-preview',
    display_name: 'Gemini 3 Flash',
    provider_display: 'Gemini',
    can_switch: false
  });
  
  // Connection status state
  const [connectionStatus, setConnectionStatus] = useState({
    calendar: { connected: false, message: 'Checking...' },
    woocommerce: { connected: false, message: 'Checking...' }
  });
  const [checkingConnection, setCheckingConnection] = useState(false);
  const [reconnecting, setReconnecting] = useState(false);
  
  // Draggable state
  const [position, setPosition] = useState({ x: 24, y: 24 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStartPos = useRef({ x: 0, y: 0 });
  const dragStartMouse = useRef({ x: 0, y: 0 });
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  
  // Check connection status on mount and periodically
  const checkConnectionStatus = useCallback(async () => {
    setCheckingConnection(true);
    try {
      const response = await authFetch(`${API}/ai/assistant/connection-status`);
      if (response.ok) {
        const data = await response.json();
        setConnectionStatus(data);
      }
    } catch (err) {
      console.error('Failed to check connection status:', err);
    } finally {
      setCheckingConnection(false);
    }
  }, []);
  
  // Force reconnect
  const handleReconnect = async () => {
    setReconnecting(true);
    try {
      const response = await authFetch(`${API}/ai/assistant/reconnect`, { method: 'POST' });
      if (response.ok) {
        const data = await response.json();
        toast.success(data.messages?.join(', ') || 'Reconnection attempted');
        // Re-check status after reconnect
        await checkConnectionStatus();
      } else {
        toast.error('Reconnection failed');
      }
    } catch (err) {
      toast.error('Reconnection error');
    } finally {
      setReconnecting(false);
    }
  };
  
  // Check connection on mount
  useEffect(() => {
    checkConnectionStatus();
    fetchAiModel();
    // Re-check every 5 minutes
    const interval = setInterval(checkConnectionStatus, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [checkConnectionStatus]);
  
  // Fetch current AI model
  const fetchAiModel = async () => {
    try {
      const response = await authFetch(`${API}/ai/current-model`);
      if (response.ok) {
        const data = await response.json();
        setAiModel(data);
      }
    } catch (err) {
      console.error('Failed to fetch AI model:', err);
    }
  };
  
  // Computed: is fully connected?
  const isFullyConnected = connectionStatus.calendar.connected && connectionStatus.woocommerce.connected;
  
  // Handle drag start
  const handleDragStart = useCallback((e) => {
    e.preventDefault();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    
    setIsDragging(true);
    dragStartPos.current = { ...position };
    dragStartMouse.current = { x: clientX, y: clientY };
  }, [position]);
  
  // Handle drag move
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDragging) return;
      
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      
      const deltaX = clientX - dragStartMouse.current.x;
      const deltaY = dragStartMouse.current.y - clientY; // Inverted for bottom positioning
      
      const newX = Math.max(10, Math.min(window.innerWidth - 80, dragStartPos.current.x + deltaX));
      const newY = Math.max(10, Math.min(window.innerHeight - 80, dragStartPos.current.y + deltaY));
      
      setPosition({ x: newX, y: newY });
    };
    
    const handleMouseUp = () => {
      if (isDragging) {
        setIsDragging(false);
      }
    };
    
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      window.addEventListener('touchmove', handleMouseMove);
      window.addEventListener('touchend', handleMouseUp);
    }
    
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchmove', handleMouseMove);
      window.removeEventListener('touchend', handleMouseUp);
    };
  }, [isDragging]);
  
  // Scroll to bottom on new messages
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);
  
  useEffect(() => {
    if (isOpen) {
      scrollToBottom();
      inputRef.current?.focus();
    }
  }, [messages, isOpen, scrollToBottom]);
  
  // Fetch chat history and settings on open
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      fetchHistory();
      fetchSettings();
    }
  }, [isOpen]);
  
  const fetchSettings = async () => {
    try {
      const response = await authFetch(`${API}/ai/assistant/settings`);
      if (response.ok) {
        const data = await response.json();
        setTemperature(data.temperature ?? 0.1);
      }
    } catch (err) {
      console.error('Failed to fetch AI settings:', err);
    }
  };
  
  const saveSettings = async (newTemp) => {
    setSavingSettings(true);
    try {
      const response = await authJsonFetch(`${API}/ai/assistant/settings`, {
        method: 'PUT',
        body: JSON.stringify({ temperature: newTemp })
      });
      if (response.ok) {
        const data = await response.json();
        setTemperature(data.temperature);
        toast.success(`Temperature set to ${data.temperature}`);
      }
    } catch (err) {
      toast.error('Failed to save settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const fetchHistory = async () => {
    try {
      const response = await authFetch(`${API}/ai/assistant/history?limit=30`);
      if (response.ok) {
        const data = await response.json();
        if (data.history && data.history.length > 0) {
          setMessages(data.history.map(h => ({
            role: h.role,
            content: h.content,
            timestamp: h.timestamp,
            exportFile: h.export_file
          })));
        }
      }
    } catch (err) {
      console.error('Failed to fetch chat history:', err);
    }
  };
  
  const sendMessage = async (e) => {
    e?.preventDefault();
    if (!input.trim() || isLoading) return;
    
    const userMessage = input.trim();
    setInput('');
    
    // Add user message to chat
    setMessages(prev => [...prev, {
      role: 'user',
      content: userMessage,
      timestamp: new Date().toISOString()
    }]);
    
    setIsLoading(true);
    
    try {
      const response = await authJsonFetch(`${API}/ai/assistant/chat`, {
        method: 'POST',
        body: JSON.stringify({ message: userMessage })
      });
      
      if (!response.ok) {
        throw new Error('Failed to get response');
      }
      
      const data = await response.json();
      
      // Add assistant response
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: data.response,
        timestamp: data.timestamp,
        exportFile: data.export_file
      }]);
      
      if (data.export_file) {
        toast.success('Data export ready for download!');
      }
      
    } catch (err) {
      console.error('Chat error:', err);
      toast.error('Sorry, I had trouble responding. Please try again.');
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: "I'm sorry, I encountered an error. Please try again in a moment.",
        timestamp: new Date().toISOString()
      }]);
    } finally {
      setIsLoading(false);
    }
  };
  
  const clearHistory = async () => {
    try {
      await authFetch(`${API}/ai/assistant/history`, {
        method: 'DELETE'
      });
      setMessages([]);
      toast.success('Chat history cleared');
    } catch (err) {
      toast.error('Failed to clear history');
    }
  };
  
  const downloadExport = (filename) => {
    // Add timestamp and auth token to prevent browser caching and handle cross-domain auth
    const token = localStorage.getItem('gs_session_token');
    const cacheBuster = `?t=${Date.now()}${token ? `&token=${token}` : ''}`;
    window.open(`${API}/ai/assistant/export/${filename}${cacheBuster}`, '_blank');
  };
  
  const openChat = () => {
    // Only open if not dragging
    if (!isDragging) {
      setIsMinimized(false);
      setIsOpen(true);
      setHasNewMessage(false);
    }
  };
  
  const minimizeChat = () => {
    setIsOpen(false);
    setIsMinimized(true);
  };
  
  // Calculate if chat should appear above or below avatar based on position
  const shouldShowBelow = position.y > window.innerHeight - 500; // If near top, show below
  
  // Welcome message suggestions
  const suggestions = [
    "How many photo days are scheduled this month?",
    "Show overdue milestones",
    "Create a new school called Example Primary",
    "What's on the calendar this week?"
  ];
  
  // Render minimized state
  if (isMinimized) {
    return (
      <MiniAssistant 
        onClick={openChat} 
        hasNewMessage={hasNewMessage} 
        position={position}
        onDragStart={handleDragStart}
        isBarbiePink={isBarbiePink}
        isConnected={isFullyConnected}
      />
    );
  }
  
  // Calculate chat panel position - show below avatar when near top of screen
  const chatStyle = shouldShowBelow 
    ? { left: position.x, top: `calc(100vh - ${position.y}px + 70px)` }  // Below avatar
    : { left: position.x, bottom: position.y + 70 };  // Above avatar (default)
  
  return (
    <div 
      style={chatStyle}
      className={cn(
        "fixed z-50 w-96 max-w-[calc(100vw-3rem)]",
        "bg-background border rounded-2xl shadow-2xl",
        "transform transition-all duration-300 ease-out",
        isOpen ? "scale-100 opacity-100" : "scale-95 opacity-0 pointer-events-none",
        isBarbiePink ? "border-pink-300" : ""
      )}
      data-testid="ai-assistant-panel"
    >
      {/* Header - Draggable */}
      <div 
        className={cn(
          "flex items-center gap-3 p-4 border-b rounded-t-2xl cursor-grab active:cursor-grabbing",
          isBarbiePink 
            ? "bg-gradient-to-r from-pink-100 to-pink-50" 
            : "bg-gradient-to-r from-blue-50 to-cyan-50"
        )}
        onMouseDown={handleDragStart}
        onTouchStart={handleDragStart}
      >
        <GripVertical className="w-4 h-4 text-muted-foreground" />
        <AssistantAvatar size="sm" isAnimated={isLoading} isBarbiePink={isBarbiePink} />
        <div className="flex-1">
          <h3 className="font-semibold text-foreground">Sophie</h3>
          <p className="text-xs text-muted-foreground">
            {isLoading ? 'Thinking...' : 'Your workflow assistant'}
          </p>
        </div>
        <div className="flex gap-1">
          <Button
            variant="ghost"
            size="icon"
            className={cn("h-8 w-8", showSettings && "bg-muted")}
            onClick={(e) => { e.stopPropagation(); setShowSettings(!showSettings); }}
            title="Settings"
            data-testid="ai-settings-btn"
          >
            <Settings className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={(e) => { e.stopPropagation(); clearHistory(); }}
            title="Clear chat"
            data-testid="ai-clear-history-btn"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={(e) => { e.stopPropagation(); minimizeChat(); }}
            data-testid="ai-minimize-btn"
          >
            <Minimize2 className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={(e) => { e.stopPropagation(); setIsOpen(false); setIsMinimized(true); }}
            data-testid="ai-close-btn"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>
      
      {/* Settings Panel */}
      {showSettings && (
        <div className="p-4 border-b bg-muted/30 space-y-4">
          {/* Connection Status */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Connection Status</span>
              <div className="flex gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleReconnect}
                  disabled={reconnecting || checkingConnection}
                  className="h-7 text-xs"
                  data-testid="sophie-reconnect-btn"
                >
                  {reconnecting ? (
                    <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                  ) : null}
                  {reconnecting ? 'Reconnecting...' : 'Refresh'}
                </Button>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-2">
              {/* Calendar Status */}
              <div className={cn(
                "p-2 rounded-lg border text-xs",
                connectionStatus.calendar.connected 
                  ? "bg-green-50 border-green-200 dark:bg-green-950 dark:border-green-800" 
                  : "bg-orange-50 border-orange-200 dark:bg-orange-950 dark:border-orange-800"
              )}>
                <div className="flex items-center gap-1.5">
                  <div className={cn(
                    "w-2 h-2 rounded-full",
                    connectionStatus.calendar.connected ? "bg-green-500" : "bg-orange-500"
                  )} />
                  <span className="font-medium">Calendar</span>
                </div>
                <p className="text-muted-foreground mt-1 truncate" title={connectionStatus.calendar.message}>
                  {connectionStatus.calendar.connected ? 'Connected' : connectionStatus.calendar.message}
                </p>
                {!connectionStatus.calendar.connected && (
                  <a href="/settings?tab=integrations" className="text-primary text-[10px] underline mt-1 block">
                    Connect in Settings
                  </a>
                )}
              </div>
              
              {/* WooCommerce Status */}
              <div className={cn(
                "p-2 rounded-lg border text-xs",
                connectionStatus.woocommerce.connected 
                  ? "bg-green-50 border-green-200 dark:bg-green-950 dark:border-green-800" 
                  : "bg-orange-50 border-orange-200 dark:bg-orange-950 dark:border-orange-800"
              )}>
                <div className="flex items-center gap-1.5">
                  <div className={cn(
                    "w-2 h-2 rounded-full",
                    connectionStatus.woocommerce.connected ? "bg-green-500" : "bg-orange-500"
                  )} />
                  <span className="font-medium">WooCommerce</span>
                </div>
                <p className="text-muted-foreground mt-1 truncate" title={connectionStatus.woocommerce.message}>
                  {connectionStatus.woocommerce.connected ? 'Connected' : connectionStatus.woocommerce.message}
                </p>
                {!connectionStatus.woocommerce.connected && (
                  <a href="/check-orders" className="text-primary text-[10px] underline mt-1 block">
                    Configure in Check Orders
                  </a>
                )}
              </div>
            </div>
          </div>
          
          {/* Current AI Model */}
          <div className="space-y-2 pt-2 border-t">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">AI Model</span>
              {aiModel.can_switch && (
                <a 
                  href="/settings?tab=ai" 
                  className="text-xs text-primary hover:underline"
                >
                  Change Model
                </a>
              )}
            </div>
            <div className={cn(
              "p-3 rounded-lg border text-sm",
              aiModel.provider === 'gemini' && "bg-blue-50 border-blue-200 dark:bg-blue-950 dark:border-blue-800",
              aiModel.provider === 'openai' && "bg-green-50 border-green-200 dark:bg-green-950 dark:border-green-800",
              aiModel.provider === 'anthropic' && "bg-purple-50 border-purple-200 dark:bg-purple-950 dark:border-purple-800"
            )}>
              <div className="flex items-center justify-between">
                <div>
                  <span className="font-medium">{aiModel.display_name}</span>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Provider: {aiModel.provider_display}
                  </p>
                </div>
                <div className={cn(
                  "w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold",
                  aiModel.provider === 'gemini' && "bg-blue-500",
                  aiModel.provider === 'openai' && "bg-green-500",
                  aiModel.provider === 'anthropic' && "bg-purple-500"
                )}>
                  {aiModel.provider === 'gemini' && 'G'}
                  {aiModel.provider === 'openai' && 'O'}
                  {aiModel.provider === 'anthropic' && 'C'}
                </div>
              </div>
            </div>
            {!aiModel.can_switch && (
              <p className="text-xs text-muted-foreground">
                Only admins can change the AI model in Settings.
              </p>
            )}
          </div>
          
          {/* Temperature Setting */}
          <div className="space-y-2 pt-2 border-t">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">AI Temperature</span>
              <span className="text-xs text-muted-foreground px-2 py-0.5 bg-muted rounded">
                {temperature.toFixed(1)}
              </span>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs text-muted-foreground">0.0</span>
              <Slider
                value={[temperature]}
                min={0}
                max={0.5}
                step={0.1}
                onValueChange={([val]) => setTemperature(val)}
                onValueCommit={([val]) => saveSettings(val)}
                disabled={savingSettings}
                className="flex-1"
                data-testid="ai-temperature-slider"
              />
              <span className="text-xs text-muted-foreground">0.5</span>
            </div>
            <p className="text-xs text-muted-foreground">
              Lower = more focused & factual. Higher = more creative.
            </p>
          </div>
        </div>
      )}
      
      {/* Messages */}
      <ScrollArea className="h-80 p-4">
        {messages.length === 0 ? (
          <div className="space-y-4">
            <div className="text-center">
              <Sparkles className={cn("w-8 h-8 mx-auto mb-2", isBarbiePink ? "text-pink-400" : "text-amber-400")} />
              <h4 className="font-medium">Hi! I'm Sophie</h4>
              <p className="text-sm text-muted-foreground mt-1">
                I can help with workflows, create records, and export data.
              </p>
            </div>
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground font-medium">Try asking:</p>
              {suggestions.map((suggestion, i) => (
                <button
                  key={i}
                  onClick={() => setInput(suggestion)}
                  className={cn(
                    "w-full text-left text-sm p-2 rounded-lg border transition-colors",
                    "hover:bg-muted/50",
                    isBarbiePink ? "hover:border-pink-300" : "hover:border-primary/30"
                  )}
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={cn(
                  "flex gap-2",
                  msg.role === 'user' ? "justify-end" : "justify-start"
                )}
              >
                {msg.role === 'assistant' && (
                  <div className="w-8 h-8 flex-shrink-0">
                    <AssistantAvatar size="sm" isBarbiePink={isBarbiePink} />
                  </div>
                )}
                <div
                  className={cn(
                    "max-w-[80%] rounded-2xl px-4 py-2",
                    msg.role === 'user' 
                      ? isBarbiePink 
                        ? "bg-pink-500 text-white rounded-tr-sm"
                        : "bg-primary text-primary-foreground rounded-tr-sm"
                      : "bg-muted rounded-tl-sm"
                  )}
                >
                  <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                  {msg.exportFile && (
                    <Button
                      variant="link"
                      size="sm"
                      className="p-0 h-auto mt-2 text-xs"
                      onClick={() => downloadExport(msg.exportFile)}
                    >
                      <Download className="w-3 h-3 mr-1" />
                      Download Excel
                    </Button>
                  )}
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="flex gap-2 justify-start">
                <div className="w-8 h-8 flex-shrink-0">
                  <AssistantAvatar size="sm" isAnimated isBarbiePink={isBarbiePink} />
                </div>
                <div className="bg-muted rounded-2xl rounded-tl-sm px-4 py-3">
                  <div className="flex gap-1">
                    <span className={cn("w-2 h-2 rounded-full animate-bounce", isBarbiePink ? "bg-pink-400" : "bg-foreground/40")} style={{ animationDelay: '0ms' }} />
                    <span className={cn("w-2 h-2 rounded-full animate-bounce", isBarbiePink ? "bg-pink-400" : "bg-foreground/40")} style={{ animationDelay: '150ms' }} />
                    <span className={cn("w-2 h-2 rounded-full animate-bounce", isBarbiePink ? "bg-pink-400" : "bg-foreground/40")} style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        )}
      </ScrollArea>
      
      {/* Input */}
      <form onSubmit={sendMessage} className="p-4 border-t">
        <div className="flex gap-2">
          <Input
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask me anything..."
            className={cn("flex-1", isBarbiePink && "focus-visible:ring-pink-500")}
            disabled={isLoading}
            data-testid="ai-chat-input"
          />
          <Button 
            type="submit" 
            size="icon"
            disabled={!input.trim() || isLoading}
            className={isBarbiePink ? "bg-pink-500 hover:bg-pink-600" : ""}
            data-testid="ai-send-btn"
          >
            {isLoading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default AIAssistant;
